import { Bell, CheckCircle2, Clock, AlertCircle } from "lucide-react";

const DAYS = ["M", "T", "W", "T", "F", "S", "S"];
const VERDICT = {
  working: "bg-emerald-100 text-emerald-800",
  "snooze-heavy": "bg-amber-100 text-amber-800",
  "often missed": "bg-rose-100 text-rose-800",
  "too early to tell": "bg-stone-100 text-stone-600",
  mixed: "bg-stone-100 text-stone-700",
};
const MODES = [
  { k: "self_started", label: "Self-started", Icon: CheckCircle2, cls: "text-emerald-700" },
  { k: "after_reminder", label: "After reminder", Icon: Bell, cls: "text-amber-700" },
  { k: "after_snooze", label: "After snooze", Icon: Clock, cls: "text-stone-600" },
];
const HINTS = {
  "snooze-heavy": { cls: "text-amber-700", text: "Often snoozed — consider a different time, or a gentler escalation so the nudge doesn't become noise." },
  "often missed": { cls: "text-rose-700", text: "Frequently missed entirely — they may not have notifications enabled, or this time doesn't fit their day." },
};

export function CheckinModes({ modes }) {
  const total = modes.self_started + modes.after_reminder + modes.after_snooze;
  if (total === 0) return null;
  return (
    <div className="grid grid-cols-3 gap-2 mb-4">
      {MODES.map(({ k, label, Icon, cls }) => (
        <div key={k} className="rounded-2xl bg-stone-50 p-3 text-center" data-testid={`mode-${k}`}>
          <Icon className={`w-4 h-4 mx-auto ${cls}`} />
          <div className="font-mono text-xl font-bold text-stone-900 mt-1">{modes[k]}</div>
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{label}</div>
          <div className="text-[10px] text-stone-400">{Math.round((modes[k] / total) * 100)}%</div>
        </div>
      ))}
    </div>
  );
}

function InsightCounts({ r }) {
  const cells = [["fired", r.fired], ["acted", r.done], ["snoozes", r.snoozes], ["dismissed", r.dismissed], ["missed", r.missed]];
  return (
    <div className="grid grid-cols-5 gap-1 mt-2 text-center">
      {cells.map(([l, v]) => (
        <div key={l} className="rounded-xl bg-stone-50 py-1.5">
          <div className="font-mono text-sm font-bold text-stone-800">{v}</div>
          <div className="text-[9px] uppercase tracking-widest text-stone-500">{l}</div>
        </div>
      ))}
    </div>
  );
}

export function InsightRow({ r }) {
  const hint = HINTS[r.verdict];
  return (
    <li data-testid={`insight-reminder-${r.id}`} className={`rounded-2xl border border-stone-100 p-3 ${r.active ? "" : "opacity-60"}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono font-bold text-stone-900">{r.time}</span>
            <span className="text-sm font-semibold text-stone-800">{r.habit_title}</span>
            {!r.active && <span className="text-[10px] text-stone-500">(off)</span>}
          </div>
          <div className="flex items-center gap-1 mt-1">
            {DAYS.map((d, i) => (
              <span key={`${d}${i}`} className={`w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center ${r.days.includes(i) ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-400"}`}>{d}</span>
            ))}
            <span className="text-[10px] text-stone-500 ml-2">{r.escalation?.enabled ? `escalates (${r.escalation.pace})` : "no escalation"} · max {r.escalation?.max_snoozes} snoozes</span>
          </div>
        </div>
        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full whitespace-nowrap ${VERDICT[r.verdict]}`} data-testid={`verdict-${r.id}`}>{r.verdict}</span>
      </div>
      <InsightCounts r={r} />
      {r.act_rate !== null && (
        <div className="mt-2 h-1.5 rounded-full bg-stone-100 overflow-hidden">
          <div className="h-full bg-emerald-500" style={{ width: `${Math.round(r.act_rate * 100)}%` }} />
        </div>
      )}
      {hint && <div className="mt-2 text-xs text-stone-600 flex items-start gap-1.5"><AlertCircle className={`w-3.5 h-3.5 mt-0.5 ${hint.cls}`} /> {hint.text}</div>}
    </li>
  );
}
